/**
 * Modal de exclusão segura de tipo de evento com confirmação por frase
 * 
 * Exibe resumo de impacto (bookings, disponibilidades, exceções) e exige
 * que o usuário digite exatamente o título do evento para confirmar.
 * Padrão enterprise SaaS de segurança para exclusões destrutivas.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Calendar, Clock, CalendarX } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { type SchedulingEventType } from '@/hooks/useSchedulingEventTypes';

const logger = createLogger('DeleteEventTypeDialog');

/** Dados de impacto retornados pela RPC */
interface DeletionImpact {
  event_title: string;
  total_bookings: number;
  confirmed_bookings: number;
  pending_bookings: number;
  availability_count: number;
  overrides_count: number;
}

interface DeleteEventTypeDialogProps {
  /** Tipo de evento a ser excluído (null = dialog fechado) */
  eventType: SchedulingEventType | null;
  /** Callback ao fechar o dialog */
  onClose: () => void;
  /** Callback ao confirmar exclusão */
  onConfirm: (id: string) => Promise<void>;
  /** Indica se a exclusão está em andamento */
  isDeleting?: boolean;
}

export function DeleteEventTypeDialog({
  eventType,
  onClose,
  onConfirm,
  isDeleting = false,
}: DeleteEventTypeDialogProps) {
  const { currentTenant } = useAuth();
  const [impact, setImpact] = useState<DeletionImpact | null>(null);
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  /** Frase que o usuário deve digitar para confirmar */
  const requiredPhrase = eventType?.title || '';

  /** Verifica se a frase digitada corresponde exatamente ao título */
  const isConfirmed = confirmText === requiredPhrase;

  // Buscar dados de impacto ao abrir o dialog
  useEffect(() => {
    if (!eventType) {
      // Resetar estado ao fechar
      setImpact(null);
      setConfirmText('');
      return;
    }

    const fetchImpact = async () => {
      setIsLoadingImpact(true);
      try {
        const { data, error } = await supabase.rpc('get_event_type_deletion_impact', {
          p_event_type_id: eventType.id,
          p_tenant_hint: currentTenant?.id,
        });

        if (error) {
          logger.error('Erro ao buscar impacto da exclusão', error);
          return;
        }

        setImpact(data as unknown as DeletionImpact);
      } catch (err) {
        logger.error('Erro inesperado ao buscar impacto', err);
      } finally {
        setIsLoadingImpact(false);
      }
    };

    fetchImpact();
  }, [eventType, currentTenant?.id]);

  /** Handler de confirmação de exclusão */
  const handleConfirm = async () => {
    if (!eventType || !isConfirmed) return;
    await onConfirm(eventType.id);
    onClose();
  };

  /** Verifica se há algum dado vinculado que será afetado */
  const hasImpact = impact && (
    impact.total_bookings > 0 ||
    impact.availability_count > 0 ||
    impact.overrides_count > 0
  );

  return (
    <Dialog open={!!eventType} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {/* Ícone de aviso destructive */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Excluir tipo de evento</DialogTitle>
              <DialogDescription>
                Você não poderá recuperar este tipo de evento após a exclusão.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Seção de impacto */}
        <div className="space-y-4">
          {isLoadingImpact ? (
            <div className="space-y-2 rounded-lg border border-border/50 bg-muted/30 p-4">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-4 w-48" />
            </div>
          ) : hasImpact ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2">
              <p className="text-sm font-medium text-destructive">
                Impacto da exclusão:
              </p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {impact.total_bookings > 0 && (
                  <li className="flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5 text-destructive" />
                    <span>
                      <strong className="text-foreground">{impact.total_bookings}</strong> agendamento(s) serão cancelados
                      {impact.confirmed_bookings > 0 && (
                        <span className="text-destructive"> ({impact.confirmed_bookings} confirmado(s))</span>
                      )}
                    </span>
                  </li>
                )}
                {impact.availability_count > 0 && (
                  <li className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-destructive" />
                    <span>
                      <strong className="text-foreground">{impact.availability_count}</strong> horário(s) de disponibilidade serão removidos
                    </span>
                  </li>
                )}
                {impact.overrides_count > 0 && (
                  <li className="flex items-center gap-2">
                    <CalendarX className="h-3.5 w-3.5 text-destructive" />
                    <span>
                      <strong className="text-foreground">{impact.overrides_count}</strong> exceção(ões) de data serão removidas
                    </span>
                  </li>
                )}
              </ul>
            </div>
          ) : impact ? (
            <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
              <p className="text-sm text-muted-foreground">
                Nenhum dado vinculado será afetado.
              </p>
            </div>
          ) : null}

          {/* Campo de confirmação por frase */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Digite <strong className="text-foreground">"{requiredPhrase}"</strong> para confirmar.
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={requiredPhrase}
              autoComplete="off"
              disabled={isDeleting}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmed || isDeleting || isLoadingImpact}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir tipo de evento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
